<template>
  <div class="container mb-4">
      <DataTable :value="actualOrderRows" :autoLayout="true">
        <template #header>
          <div class="p-d-flex p-jc-between">
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon" style="color: black">
                {{INVOICE_BY_ID(id).name}}
              </span>
              <span class="p-inputgroup-addon">
                <i class="pi pi-calendar"></i>
              </span>
              <InputText v-model="invoiceDate" placeholder="Fakturadatum" />
              <span class="p-inputgroup-addon">
                <i class="pi pi-id-card"></i>
              </span>
              <InputText v-model="deliveryPeriod" placeholder="Leveransperiod" />
              <span class="p-inputgroup-addon">
                <i class="pi pi-id-card"></i>
              </span>
              <InputText v-model="marking" placeholder="Inköpsnr" />
              <span class="p-inputgroup-addon">
                <i class="pi pi-money-bill"></i>
              </span>
              <DropDown v-model="dmt" :options="INVOICE_DMTS" optionLabel="data.dmtString" />
            </div>
            <div>
              <Button label="Generera faktura" icon="pi pi-refresh" @click="recalculateInvoice()" />
              <template v-if="archive">
                <Button label="Uppdatera faktura" icon="pi pi-plus" :disabled="actualOrderRows.length == 0" @click="updateInvoice()" />
              </template>
              <template v-else>
                <Button label="Spara faktura" icon="pi pi-plus" :disabled="saved || (actualOrderRows.length == 0)" @click="saveInvoice()"/>
                <Button label="Skicka faktura" icon="pi pi-check" :disabled="!saved || INVOICE_BY_ID(id).state !== 'saved'" @click="sendInvoice(INVOICE_BY_ID(id).name)" />
              </template>
            </div>
          </div>
        </template>
        <Column field="article" header="Artikel" />
        <Column field="text" header="Text" />
        <Column field="amount" header="Antal">
          <template #body="slotProps">
            {{slotProps.data.amount.toFixed(2)}}
          </template>
        </Column>
        <Column field="price" header="Apris">
          <template #body="slotProps">
            {{slotProps.data.price.toFixed(2)}}
          </template>
        </Column>
        <Column>
          <template #header>
            Belopp
          </template>
          <template #body="slotProps">
            {{slotProps.data.amount === '' ? '' : (slotProps.data.amount * slotProps.data.price).toFixed(2)}}
          </template>
        </Column>
        <template #footer>
          Total: {{actualOrderRows.reduce((prev, curr) => curr.tag === 'Separator' ? prev : prev + (parseFloat(curr.amount) * curr.price), 0).toFixed(2)}}
        </template>
      </DataTable>
      <InvoiceFeralcoInternCreateSelf v-if="!_.isEmpty(subContractorOrderRows)" :id="isimGenerateId(subContractorId)" :invoice="subContractorOrderRows" :infoText="subContractorInfoText" :printable="saved" />
  </div>
</template>

<script>

import { mapGetters } from 'vuex'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import DropDown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import moment from 'moment'

import InvoiceFeralcoInternCreateSelf from './InvoiceFeralcoInternCreateSelf'

import { FeralcoInternMixin } from '@/mixins/FeralcoInternMixin'
import { InvoiceDmtMixin } from '@/mixins/InvoiceDmtMixin'
import { RoundingMixin } from '@/mixins/RoundingMixin'
import { InvoiceSubIdMixin } from '@/mixins/InvoiceSubIdMixin'
import { InvoiceSubContractorMixin } from '@/mixins/InvoiceSubContractorMixin'

export default {
  name: 'InvoiceFeralcoInternCreate',
  mixins: [FeralcoInternMixin, InvoiceDmtMixin, RoundingMixin, InvoiceSubIdMixin, InvoiceSubContractorMixin],
  components: {
    DataTable,
    Column,
    Button,
    DropDown,
    InputText,
    InvoiceFeralcoInternCreateSelf
  },
  props: {
    id: {
      type: Number,
      required: true
    },
    archive: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      actualOrderRows: [],
      invoiceDate: moment().format('YYYY-MM-DD'),
      deliveryPeriod: '',
      marking: '',
      deliveryPlace: '',
      saved: false
    }
  },
  computed: {
    ...mapGetters(['INVOICES', 'INVOICE_BY_ID', 'VISMA_ARTICLES', 'VISMA_ARTICLE_BY_ID', 'TRUCKS_BY_ID'])
  },
  async mounted () {
    await this.$store.dispatch('GET_ACTIVE_INVOICES')
    await this.$store.dispatch('GET_TRUCKS')
    await this.$store.dispatch('GET_INVOICE_ID', { id: this.$props.id })
    const invoice = this.INVOICE_BY_ID(this.$props.id)
    if (invoice !== undefined && invoice.data !== null) {
      const invoiceData = invoice.data
      this.actualOrderRows = invoiceData.orderRows ? invoiceData.orderRows : null
      this.deliveryPeriod = invoiceData.period ? invoiceData.period : ''
      this.marking = invoiceData.marking ? invoiceData.marking : ''
      this.deliveryPlace = invoiceData.deliveryPlace ? invoiceData.deliveryPlace : ''
      this.saved = true
      const backupDmt = this.dmt
      this.dmt = this.INVOICE_DMTS_BY_ID(invoiceData.dmtId)
      this.dmt = this.dmt === undefined ? backupDmt : this.dmt
      this.iscmInitData(invoice)
    }
  },
  methods: {
    recalculateInvoice () {
      this.saved = false
      this.actualOrderRows = this.createVismaInvoice(this.$props.id)
    },
    getOrders (id) {
      return this.$store.getters.INVOICE_BY_ID(id).invoiceDatas.map(o => o.Order)
    },
    getOrderData (id) {
      return this.INVOICE_BY_ID(id).invoiceDatas.map(flat => flat.Order.data)
    },
    createVismaInvoice (id) {
      let orderRows = []
      let start = Number.MAX_VALUE
      let end = Number.MIN_VALUE
      let scOrderRows = []
      this.getOrderData(id).forEach(element => {
        this.marking = element.inkopsNr
        this.deliveryPlace = element.deliveryPlaces
        const dDate = new Date(element.dateDelivery)
        start = new Date(Math.min(...[dDate, start]))
        end = new Date(Math.max(...[dDate, end]))
        const art = this.decideArticle(element)
        const idx = orderRows.findIndex(el => el.article === art.article)
        if (idx >= 0) {
          orderRows[idx].amount += art.amount
        } else {
          orderRows.push({...art})
        }
        const subContractor = this.TRUCKS_BY_ID(element.truck).data.subcontractor
        if (subContractor === 191) {
          const idx2 = scOrderRows.findIndex(el => el.article === art.article)
          if (idx2 >= 0) {
            scOrderRows[idx2].amount += art.amount
          } else {
            scOrderRows.push({...art})
          }
        }
      })

      orderRows.forEach(element => (element.amount = this.roundUp(element.amount, 2)))
      scOrderRows.forEach(element => (element.amount = this.roundUp(element.amount, 2)))

      this.deliveryPeriod = moment(start).format('YYYY-MM-DD') + '--' + moment(end).format('YYYY-MM-DD')

      this.iscmCreateInvoice(
        scOrderRows,
        191,
        this.invoiceDate,
        this.getDmtString(),
        this.getDmtId(),
        this.deliveryPeriod,
        this.marking,
        this.deliveryPlace,
        this.infoText)
      return orderRows
    },
    decideArticle (order) {
      const amount = this.fimCalculateLoadedAmount(order)
      if (order.loadingPlaces === 'Inovyn' && order.deliveryPlaces === 'Vetlanda') {
        return this.createInovynVetlArticle(amount)
      } else if (order.loadingPlaces === 'Inovyn' && order.deliveryPlaces === 'Sölvesborg') {
        return this.createInovynSolvArticle(amount)
      } else if (order.loadingPlaces === 'Kemira' && order.deliveryPlaces === 'Sölvesborg') {
        return order.retur ? this.createKemiraSolvReturnArticle(amount) : this.createKemiraSolvExtraArticle(amount)
      } else if (order.loadingPlaces === 'Kemira' && order.deliveryPlaces === 'Vetlanda') {
        return order.retur ? this.createKemiraVetlReturnArticle(amount) : this.createKemiraVetlaExtraArticle(amount)
      }
    },
    createInovynVetlArticle (amount) {
      const price = 212
      return {
        article: '995401',
        text: 'Saltsyra, Innovyn, Stenungsund (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    createInovynSolvArticle (amount) {
      const price = 303
      return {
        article: '995402',
        text: 'Saltsyra, Innovyn, Stenungsund (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    createKemiraSolvReturnArticle (amount) {
      const price = 153
      return {
        article: '896201',
        text: 'Saltsyra, Kemira, Helsingborg, retur (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    createKemiraSolvExtraArticle (amount) {
      const price = 218
      return {
        article: '896202',
        text: 'Saltsyra, Kemira, Helsingborg, ej retur (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    createKemiraVetlReturnArticle (amount) {
      const price = 224
      return {
        article: '996201',
        text: 'Saltsyra, Kemira, Helsingborg, retur (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    createKemiraVetlaExtraArticle (amount) {
      const price = 377
      return {
        article: '996202',
        text: 'Saltsyra, Kemira, Helsingborg, ej retur (' + price + ' exkl DMT)',
        price: this.roundUp(price * this.getDmtValue(), 2),
        amount: amount
      }
    },
    async saveInvoice () {
      this.saved = true
      await this.$store.dispatch('SAVE_INVOICE_DATA', {
        id: this.$props.id,
        data: {
          customer: 1501,
          invoiceDate: this.invoiceDate,
          dmt: this.getDmtString(),
          dmtId: this.getDmtId(),
          period: this.deliveryPeriod,
          marking: this.marking,
          deliveryPlace: this.deliveryPlace,
          orderRows: this.actualOrderRows
        }
      })
      await this.iscmSaveInvoice(this.INVOICE_BY_ID(this.$props.id), 'feralcoIntern')
      await this.$store.dispatch('GET_ACTIVE_INVOICES')
    },
    async updateInvoice () {
      await this.$store.dispatch('UPDATE_INVOICE_DATA', {
        id: this.$props.id,
        data: {
          customer: 1501,
          invoiceDate: this.invoiceDate,
          dmt: this.getDmtString(),
          dmtId: this.getDmtId(),
          period: this.deliveryPeriod,
          marking: this.marking,
          deliveryPlace: this.deliveryPlace,
          orderRows: this.actualOrderRows
        }
      })
      await this.iscmSaveInvoice(this.INVOICE_BY_ID(this.$props.id), 'feralcoIntern')
      await this.$store.dispatch('GET_ACTIVE_INVOICES')
      this.$router.go(-1)
    },
    async sendInvoice () {
      await this.$http.post('/api/vismasync/invoice', this.INVOICE_BY_ID(this.$props.id).data)
    }
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/Invoices/InvoiceFeralcoInternCreate.vue